/**
 * Analytics Service - PostHog Integration
 *
 * Provides centralized analytics tracking for KPI metrics.
 * Integrates with PostHog for product analytics, feature flags, and session recording.
 *
 * KPIs tracked (FIR-85):
 * - Browse -> Borrow request funnel
 * - Borrow completion rate
 * - Late fee occurrences
 * - Repeat borrower rate
 * - Page views and user engagement
 *
 * @see https://posthog.com/docs/libraries/js
 */

import posthog from 'posthog-js';

// PostHog initialization flag
let isInitialized = false;

/**
 * Initialize PostHog analytics
 * Called once at app startup
 */
export function initAnalytics() {
  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  const apiHost =
    process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (!apiKey) {
    console.warn('[Analytics] PostHog key not configured. Analytics disabled.');
    return;
  }

  if (isInitialized) {
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    // Capture page views automatically
    capture_pageview: true,
    // Enable session recording (opt-in for privacy)
    disable_session_recording: true,
    // Respect Do Not Track browser setting
    respect_dnt: true,
    // Persist across sessions
    persistence: 'localStorage+cookie',
    // Enable autocapture for clicks (disabled for privacy by default)
    autocapture: false,
    // Load feature flags on init
    bootstrap: {},
  });

  isInitialized = true;
}

/**
 * Identify a user for analytics tracking
 * @param {string} userId - The user's unique ID
 * @param {Object} properties - Optional user properties
 */
export function identifyUser(
  userId: string,
  properties: Record<string, unknown> = {}
) {
  if (!isInitialized) return;

  posthog.identify(userId, {
    ...properties,
    identified_at: new Date().toISOString(),
  });
}

/**
 * Reset analytics identity (on logout)
 */
export function resetAnalytics() {
  if (!isInitialized) return;
  posthog.reset();
}

// ============================================================================
// GAME LIBRARY KPI EVENTS (FIR-85)
// ============================================================================

/**
 * Track game view
 * Part of: Browse -> Borrow funnel
 */
export function trackGameView(
  gameId: string,
  gameName: string,
  source: string = 'browse'
) {
  if (!isInitialized) return;

  posthog.capture('game_viewed', {
    game_id: gameId,
    game_name: gameName,
    source,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track borrow request initiated
 * Part of: Browse -> Borrow funnel
 */
export function trackBorrowRequestStart(
  gameId: string,
  gameName: string,
  ownerId: string
) {
  if (!isInitialized) return;

  posthog.capture('borrow_request_started', {
    game_id: gameId,
    game_name: gameName,
    owner_id: ownerId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track borrow request submitted
 * Part of: Browse -> Borrow funnel
 */
export function trackBorrowRequestSubmitted(
  gameId: string,
  gameName: string,
  requestId: string
) {
  if (!isInitialized) return;

  posthog.capture('borrow_request_submitted', {
    game_id: gameId,
    game_name: gameName,
    request_id: requestId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track borrow request approved (by owner)
 */
export function trackBorrowRequestApproved(requestId: string, gameId: string) {
  if (!isInitialized) return;

  posthog.capture('borrow_request_approved', {
    request_id: requestId,
    game_id: gameId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track borrow completion (game shipped to borrower)
 * KPI: Borrow completion rate
 */
export function trackBorrowComplete(
  requestId: string,
  gameId: string,
  gameName: string
) {
  if (!isInitialized) return;

  posthog.capture('borrow_completed', {
    request_id: requestId,
    game_id: gameId,
    game_name: gameName,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track game return initiated
 */
export function trackReturnInitiated(requestId: string, gameId: string) {
  if (!isInitialized) return;

  posthog.capture('return_initiated', {
    request_id: requestId,
    game_id: gameId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track game return completed
 */
export function trackReturnComplete(requestId: string, gameId: string) {
  if (!isInitialized) return;

  posthog.capture('return_completed', {
    request_id: requestId,
    game_id: gameId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track overdue notification sent
 * KPI: Late fee occurrences
 */
export function trackOverdueNotification(
  requestId: string,
  daysOverdue: number,
  lateFees: number
) {
  if (!isInitialized) return;

  posthog.capture('overdue_notification_sent', {
    request_id: requestId,
    days_overdue: daysOverdue,
    late_fees: lateFees,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track late fee payment
 * KPI: Late fee recovery
 */
export function trackLateFeePayment(requestId: string, amount: number) {
  if (!isInitialized) return;

  posthog.capture('late_fee_paid', {
    request_id: requestId,
    amount,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track user as repeat borrower
 * KPI: Repeat borrower rate
 */
export function trackRepeatBorrower(userId: string, borrowCount: number) {
  if (!isInitialized) return;

  posthog.capture('repeat_borrower', {
    user_id: userId,
    borrow_count: borrowCount,
    timestamp: new Date().toISOString(),
  });

  // Update user property for segmentation
  posthog.people.set({
    total_borrows: borrowCount,
    is_repeat_borrower: borrowCount > 1,
  });
}

// ============================================================================
// GAME UPLOAD & LIBRARY EVENTS
// ============================================================================

/**
 * Track game uploaded to library
 */
export function trackGameUploaded(
  gameId: string,
  gameName: string,
  libraryId: string
) {
  if (!isInitialized) return;

  posthog.capture('game_uploaded', {
    game_id: gameId,
    game_name: gameName,
    library_id: libraryId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track library created
 */
export function trackLibraryCreated(libraryId: string, libraryName: string) {
  if (!isInitialized) return;

  posthog.capture('library_created', {
    library_id: libraryId,
    library_name: libraryName,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track search performed
 */
export function trackSearch(
  query: string,
  resultsCount: number,
  filters: Record<string, unknown> = {}
) {
  if (!isInitialized) return;

  posthog.capture('search_performed', {
    query,
    results_count: resultsCount,
    filters,
    timestamp: new Date().toISOString(),
  });
}

// ============================================================================
// PAYMENT EVENTS
// ============================================================================

/**
 * Track checkout started
 */
export function trackCheckoutStarted(
  type: string,
  amount: number,
  gameId: string
) {
  if (!isInitialized) return;

  posthog.capture('checkout_started', {
    type, // 'rental', 'purchase', 'late_fee'
    amount,
    game_id: gameId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track payment success
 */
export function trackPaymentSuccess(
  type: string,
  amount: number,
  gameId: string
) {
  if (!isInitialized) return;

  posthog.capture('payment_completed', {
    type,
    amount,
    game_id: gameId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track payment failed
 */
export function trackPaymentFailed(
  type: string,
  amount: number,
  error: unknown
) {
  if (!isInitialized) return;

  posthog.capture('payment_failed', {
    type,
    amount,
    error: error instanceof Error ? error.message : 'Unknown error',
    timestamp: new Date().toISOString(),
  });
}

// ============================================================================
// USER ENGAGEMENT EVENTS
// ============================================================================

/**
 * Track page view (manual, for SPA navigation)
 */
export function trackPageView(
  pageName: string,
  properties: Record<string, unknown> = {}
) {
  if (!isInitialized) return;

  posthog.capture('$pageview', {
    page_name: pageName,
    ...properties,
  });
}

/**
 * Track feature usage
 */
export function trackFeatureUsed(
  featureName: string,
  properties: Record<string, unknown> = {}
) {
  if (!isInitialized) return;

  posthog.capture('feature_used', {
    feature: featureName,
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track error occurrence
 */
export function trackError(
  errorType: string,
  errorMessage: string,
  context: Record<string, unknown> = {}
) {
  if (!isInitialized) return;

  posthog.capture('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...context,
    timestamp: new Date().toISOString(),
  });
}

// ============================================================================
// FEATURE FLAGS
// ============================================================================

/**
 * Check if a feature flag is enabled
 * @param {string} flagKey - The feature flag key
 * @returns {boolean} Whether the flag is enabled
 */
export function isFeatureEnabled(flagKey: string): boolean {
  if (!isInitialized) return false;
  return posthog.isFeatureEnabled(flagKey) ?? false;
}

/**
 * Get feature flag value
 * @param {string} flagKey - The feature flag key
 * @param {*} defaultValue - Default value if flag not found
 * @returns {*} The flag value
 */
export function getFeatureFlag<T = unknown>(
  flagKey: string,
  defaultValue: T | null = null
): T | null {
  if (!isInitialized) return defaultValue;
  return (posthog.getFeatureFlag(flagKey) as T) ?? defaultValue;
}

// Export posthog instance for advanced usage
export { posthog };

// Default export for convenience
export default {
  init: initAnalytics,
  identify: identifyUser,
  reset: resetAnalytics,
  trackGameView,
  trackBorrowRequestStart,
  trackBorrowRequestSubmitted,
  trackBorrowRequestApproved,
  trackBorrowComplete,
  trackReturnInitiated,
  trackReturnComplete,
  trackOverdueNotification,
  trackLateFeePayment,
  trackRepeatBorrower,
  trackGameUploaded,
  trackLibraryCreated,
  trackSearch,
  trackCheckoutStarted,
  trackPaymentSuccess,
  trackPaymentFailed,
  trackPageView,
  trackFeatureUsed,
  trackError,
  isFeatureEnabled,
  getFeatureFlag,
};
